/* eslint-disable no-nested-ternary */
/* eslint-disable indent */
// Libraries

import { fetchEntityListRequest } from 'containers/MerchantOnboard/redux/merchantOnBoard.actions';

// Layouts

// Components
import {
    FormElems,
    CustomDate,
    CustomSelect,
    GraphSwitch,
    CardWithTab,
    SuccessCard,
    FailureCard,
} from '../../components';

// Constants
import {
    trendLegends,
    bankLegends,
    barLegends,
    successRateTab,
    failureRateTab,
    ENTITY_TYPE,
} from '../../utils/constants/commonValues';

// Helper
import {
    getFormattedDate,
    formatPieChartData,
} from '../../utils/helperMethods';

// Images
import barLoader from '../../static/images/skeletons/bar.gif';
import lineLoader from '../../static/images/skeletons/line.gif';
import lineBigLoader from '../../static/images/skeletons/line-big.gif';

// Actions
import SuccessRate from '../../static/images/SuccessRate.svg';
import InfoSvg from '../../static/images/info.svg';
import Transaction from '../../static/images/tranx.svg';
import TransactionCount from '../../static/images/tranxCount.svg';
import TotalGmv from '../../static/images/gmv.svg';

import { fetchUnderlyingMerchantStaticError } from '../../redux/common/staticData/staticData.actions';
import {
    fetchMixedGraphRequest,
    fetchMultiGraphRequest,
    fetchSingleGraphRequest,
    fetchDashboardSummaryRequest,
    resetDashboardRequest,
    fetchMultiEntityHierarchyRequest,
} from './redux/dashboard.actions';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { components } from 'react-select';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo, useCallback } from 'react';

// Styles
import './dashboard.scss';

const ValueContainer = ({ children, ...props }) => {
    const { getValue, hasValue, options } = props;
    const newChildren = [...children];
    const nbValues = getValue().filter(item => item.value !== 'all').length;
    const type = options[0]?.type;
    if (type === 'merchant') {
        newChildren[0] = `${nbValues} Merchants Selected`;
    } else {
        newChildren[0] = `${nbValues} Aggregators Selected`;
    }

    if (!hasValue) {
        return (
            <components.ValueContainer {...props}>
                {children}
            </components.ValueContainer>
        );
    }
    return (
        <components.ValueContainer {...props}>
            {newChildren}
        </components.ValueContainer>
    );
};

const getEntityIds = entity => {
    if (entity) {
        return entity.map(item => item.value);
    }
    return [];
};

const Option = props => {
    const { data, isSelected } = props;
    data.checked = isSelected;
    const dataOptions = [data];
    return (
        <components.Option {...props}>
            <div className={data.value}>
                <FormElems.Checkbox
                    name={data.value}
                    options={dataOptions || []}
                    valueField="value"
                    labelField="label"
                    checked={isSelected}
                />
            </div>
        </components.Option>
    );
};

const formatOptions = staticData => {
    if (!isEmpty(staticData.underlyingMerchant)) {
        const formatMerchant = staticData.underlyingMerchant.map(item => {
            return {
                label: item.name,
                value: item.entityCode,
                checked: false,
                type: 'merchant',
            };
        });

        return formatMerchant;
    }
    if (!isEmpty(staticData.underlyingMerchantID)) {
        const formatMerchant = staticData.underlyingMerchantID.map(item => {
            return {
                label: item.entityCode,
                value: item.entityCode,
                checked: false,
                type: 'merchant',
            };
        });

        return formatMerchant;
    }
    if (!isEmpty(staticData.aggregator)) {
        const formatAggregator = staticData.aggregator.map(item => {
            return {
                label: item.name,
                value: item.entityCode,
                checked: false,
                type: 'aggregator',
            };
        });

        return formatAggregator;
    }
    return [];
};

// eslint-disable-next-line complexity
const Dashboard = props => {
    const { staticData, actions, dashboard, merchantData, auth } = props;
    const {
        error,
        summary,
        singleGraph: { GMV, STATUS_TREND, SUCCESS_RATE, TRANSACTIONS_COUNT },
        paymentModeWise,
        failures,
    } = dashboard;
    const { userLevel, associatedEntityId, name } = auth.userDetails;
    const [selectedMerchant, setSelectedMerchant] = useState([]);
    const [selectedMerchantID, setSelectedMerchantID] = useState([]);
    const [dateValue, setDateValue] = useState('');
    const [prevDateValue, setPrevDateValue] = useState('');
    const [merchantOptions, setMerchantOptions] = useState([]);
    const [merchantOptionsID, setMerchantOptionsID] = useState([]);
    const { entityList } = merchantData;
    const [aggregatorOptions, setAggregatorOptions] = useState([]);
    const [selectedAggregator, setSelectedAggregator] = useState([]);

    // fetch entity (aggregator)
    useEffect(() => {
        // get all aggregator
        actions.fetchEntityListRequest(ENTITY_TYPE.AGGREGATOR);
        actions.fetchEntityListRequest(ENTITY_TYPE.MERCHANT);
    }, [actions]);

    useEffect(() => {
        if (entityList?.aggregator?.length) {
            setAggregatorOptions(formatOptions(entityList));
        }
    }, [entityList, entityList.aggregator]);

    // handle scenerio when user selects aggregator
    useEffect(() => {
        if (entityList?.merchants?.length && selectedAggregator.length) {
            const formattedMerchants = formatOptions({
                underlyingMerchant: entityList.merchants,
            });
            setMerchantOptions(formattedMerchants);
        }
    }, [entityList, entityList.merchants, staticData, selectedAggregator]);

    useEffect(() => {
        if (entityList?.merchants?.length && selectedAggregator.length) {
            const formattedMerchants = formatOptions({
                underlyingMerchantID: entityList.merchants,
            });
            setMerchantOptionsID(formattedMerchants);
        }
    }, [entityList, entityList.merchants, staticData, selectedAggregator]);

    useEffect(() => {
        return () => {
            actions.fetchUnderlyingMerchantStaticError({ error: '' });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // for aggregator show aggregator's data default
        if (userLevel === 'AGGREGATOR') {
            const currentUserEntity = formatOptions({
                underlyingMerchant: [
                    {
                        entityCode: associatedEntityId,
                        name,
                    },
                ],
            });

            setSelectedAggregator(currentUserEntity);
        }

        if (entityList.merchant && entityList.merchant.length) {
            const merchantList = formatOptions({
                underlyingMerchant: entityList.merchant || [],
            });
            setMerchantOptions(merchantList);

            if (userLevel === 'MLE' || userLevel === 'FRANCHISE') {
                const defaultSelectedMerchant =
                    merchantList &&
                    merchantList.length &&
                    merchantList.map(item => ({ ...item, checked: true }));
                setSelectedMerchant(defaultSelectedMerchant);
            }
        }

        if (entityList.merchant && entityList.merchant.length) {
            const merchantList = formatOptions({
                underlyingMerchantID: entityList.merchant || [],
            });
            setMerchantOptionsID(merchantList);

            if (userLevel === 'MLE' || userLevel === 'FRANCHISE') {
                const defaultSelectedMerchant =
                    merchantList &&
                    merchantList.length &&
                    merchantList.map(item => ({ ...item, checked: true }));
                setSelectedMerchantID(defaultSelectedMerchant);
            }
        }

        if (
            staticData.underlyingMerchant &&
            staticData.underlyingMerchant.length &&
            userLevel === 'MERCHANT'
        ) {
            const merchantList = formatOptions(staticData);
            setMerchantOptions(merchantList);

            const merchantListID = formatOptions({...staticData, underlyingMerchantID: staticData.underlyingMerchant });
            setMerchantOptionsID(merchantListID);

            const defaultSelectedMerchant =
                merchantList &&
                merchantList.length &&
                merchantList.map(item => ({ ...item, checked: true }));

            setSelectedMerchant(defaultSelectedMerchant);
            setSelectedMerchantID(defaultSelectedMerchant);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        entityList.merchant,
        userLevel,
        associatedEntityId,
        name,
        staticData.underlyingMerchant,
    ]);

    const handleMerchantSelect = value => {
        setSelectedMerchant(value);
    };

    const handleMerchantSelectID = value => {
        setSelectedMerchantID(value);
    };
    // aggregator select
    const handleAggregatorSelect = val => {
        setSelectedAggregator(val);
        const parsedAggregatorVal = val.map(aggregator => aggregator.value);
        actions.fetchMultiEntityHierarchyRequest(
            parsedAggregatorVal,
            'LOWER_COMPLETE',
            'AGGREGATOR_TXN'
        );
        setSelectedMerchant([]);
        if (!val.length) {
            setMerchantOptions(
                formatOptions({ underlyingMerchant: entityList.merchant })
            );
        }
    };

    const memoMerchantData = useMemo(() => {
        if (
            selectedMerchant &&
            selectedMerchant.length &&
            dateValue &&
            dateValue.value
        ) {
            return {
                selectedMerchant,
                dateValue,
            };
        }
    }, [selectedMerchant, dateValue]);

    const memoMerchantDataID = useMemo(() => {
        if (
            selectedMerchantID &&
            selectedMerchantID.length &&
            dateValue &&
            dateValue.value
        ) {
            return {
                selectedMerchant: selectedMerchantID,
                dateValue,
            };
        }
    }, [selectedMerchantID, dateValue]);

    console.log('selectedMerchantID', selectedMerchantID);

    const memoAggregatorData = useMemo(() => {
        if (
            selectedAggregator &&
            selectedAggregator.length &&
            dateValue &&
            dateValue.value
        ) {
            return {
                selectedAggregator,
                dateValue,
            };
        }
    }, [selectedAggregator, dateValue]);

    const fetchDasboardData = useCallback(
        data => {
            actions.fetchMixedGraphRequest(data);
            actions.fetchMultiGraphRequest(data);
            actions.fetchSingleGraphRequest({
                ...data,
                graphIdentifier: 'GMV',
            });
            actions.fetchSingleGraphRequest({
                ...data,
                graphIdentifier: 'STATUS_TREND',
            });
            actions.fetchSingleGraphRequest({
                ...data,
                graphIdentifier: 'SUCCESS_RATE',
            });
            actions.fetchSingleGraphRequest({
                ...data,
                graphIdentifier: 'TRANSACTIONS_COUNT',
            });
            actions.fetchDashboardSummaryRequest(data);
        },
        [actions]
    );

    useEffect(() => {
        console.log(memoMerchantDataID, "memoMerchantDataID");
        if (
            (memoMerchantData &&
                memoMerchantData.selectedMerchant &&
                memoMerchantData.selectedMerchant.length &&
                memoMerchantData.dateValue &&
                memoMerchantData.dateValue.value) ||
            (memoAggregatorData &&
                memoAggregatorData.selectedAggregator &&
                memoAggregatorData.selectedAggregator.length &&
                memoAggregatorData.dateValue &&
                memoAggregatorData.dateValue.value) ||
            (memoMerchantDataID &&
                memoMerchantDataID.selectedMerchant &&
                memoMerchantDataID.selectedMerchant.length &&
                memoMerchantDataID.dateValue &&
                memoMerchantDataID.dateValue.value)
        ) {
            let merchantIds = getEntityIds(
                memoMerchantData?.selectedMerchant
            );
            const merchantIds2 = getEntityIds(
                memoMerchantDataID?.selectedMerchant
            );

            merchantIds = [...new Set([...merchantIds, ...merchantIds2])];

            const aggregatorIds = getEntityIds(
                memoAggregatorData?.selectedAggregator
            );
            let entityIds = [];
            // if merchant and aggregator only show merchant
            if (merchantIds.length && aggregatorIds.length) {
                entityIds = merchantIds;
            } else if (aggregatorIds.length && !merchantIds.length) {
                entityIds = aggregatorIds;
            } else if (merchantIds.length && !aggregatorIds.length) {
                entityIds = merchantIds;
            }
            let date;
            if (memoMerchantData?.dateValue) {
                date = getFormattedDate(memoMerchantData?.dateValue);
            } else if (memoAggregatorData?.dateValue) {
                date = getFormattedDate(memoAggregatorData?.dateValue);
            }else if (memoMerchantDataID?.dateValue) {
                date = getFormattedDate(memoMerchantDataID?.dateValue);
            }
            const data = {
                endDate: date.endDate,
                startDate: date.startDate,
                merchantIds: entityIds,
            };

            fetchDasboardData(data);
        }
        return () => {
            actions.resetDashboardRequest();
        };
    }, [
        actions,
        fetchDasboardData,
        memoMerchantData,
        memoAggregatorData,
        userLevel,
        associatedEntityId,
        memoMerchantDataID
    ]);

    // erroemsg show condition
    const noDataSelection = () => {
        if (error.errorMsg || !dateValue || !dateValue.value) {
            return true;
        }

        if (selectedAggregator.length || selectedMerchant.length || selectedMerchantID.length) {
            return false;
        }

        return true;
    };

    const onInputChangeAgre = (value) => {
        const filtered = entityList.aggregator.filter(item => {
            return item.name.toLowerCase().includes(value.toLowerCase());
        }) || [];

        const data = {
            entityName: value,
            entityCode: '',
        };

        if (filtered.length === 0 && value.length >= 3) {
            actions.fetchEntityListRequest(ENTITY_TYPE.AGGREGATOR, data);
        }
    }

    const onInputChange = (value) => {
        const filtered = entityList.merchant.filter(item => {
            return item.name.toLowerCase().includes(value.toLowerCase());
        }) || [];

        const data = {
            entityName: value,
            entityCode: '',
        };

        if (filtered.length === 0 && value.length >= 3) {
            actions.fetchEntityListRequest(ENTITY_TYPE.MERCHANT, data);
        }
    }

    const onInputChangeID = (value) => {
        const filtered = entityList.merchant.filter(item => {
            return item.entityCode.toLowerCase().includes(value.toLowerCase());
        }) || [];

        const data = {
            entityName: '',
            entityCode: value,
        };

        if (filtered.length === 0 && value.length >= 3) {
            actions.fetchEntityListRequest(ENTITY_TYPE.MERCHANT, data);
        }
    }

    return (
        <div className="dashboard-wrapper">
            <div className="profile-header clearfix">
                <h3 className="page-tile">Dashboard</h3>
                <div className="page-actions">
                    {userLevel === 'ADMIN' && (
                        <CustomSelect
                            name="entityCodes"
                            customClass="wrapper-select aggregator"
                            options={aggregatorOptions}
                            components={{ Option, ValueContainer }}
                            isMulti
                            isSearchable
                            data={selectedAggregator}
                            value={selectedAggregator}
                            onChange={handleAggregatorSelect}
                            closeMenuOnSelect={false}
                            placeholder="Select Aggregator"
                            onInputChange={onInputChangeAgre}
                        />
                    )}

                    {userLevel && userLevel !== 'MERCHANT' && (
                        <>
                            <CustomSelect
                                name="entityCodes"
                                customClass="wrapper-select merchant"
                                options={merchantOptions}
                                components={{ Option, ValueContainer }}
                                isMulti
                                isSearchable
                                data={selectedMerchant}
                                value={selectedMerchant}
                                onChange={handleMerchantSelect}
                                closeMenuOnSelect={false}
                                placeholder="Select Merchant Name"
                                onInputChange={onInputChange}
                            />
                            <CustomSelect
                                name="entityCodes"
                                customClass="wrapper-select merchant"
                                options={merchantOptionsID}
                                components={{ Option, ValueContainer }}
                                isMulti
                                isSearchable
                                data={selectedMerchantID}
                                value={selectedMerchantID}
                                onChange={handleMerchantSelectID}
                                closeMenuOnSelect={false}
                                placeholder="Select Merchant ID"
                                onInputChange={onInputChangeID}
                            />
                        </>
                    )}

                    <CustomDate
                        setDateValue={setDateValue}
                        setPrevDateValue={setPrevDateValue}
                        prevDateValue={prevDateValue}
                        defaultSelected={{ label: 'Today', value: 'today' }}
                    />
                </div>
            </div>
            {noDataSelection() ? (
                <div className="noDate-selection">
                    <img src={InfoSvg} alt="info" />
                    <p>
                        {error.errorMsg ||
                            'Please select merchants and date to view the stats'}
                    </p>
                </div>
            ) : (
                <>
                    <ul className="summary-list">
                        <li className="summary-card">
                            <div className="summary-label">
                                <label>TOTAL PAYMENT VOLUME</label>
                                <p>
                                    {summary.isLoading ? (
                                        <span className="summary-skeleton" />
                                    ) : summary.GMV ? (
                                        `₹ ${summary.GMV}`
                                    ) : (
                                        'NA'
                                    )}
                                    {/* <span className="indicator">0.34%</span> */}
                                </p>
                            </div>
                            <div className="summary-icon">
                                <img src={TotalGmv} alt="Total GMV" />
                            </div>
                        </li>
                        <li className="summary-card">
                            <div className="summary-label">
                                <label> TRANSACTIONS COUNT</label>
                                <p>
                                    {summary.isLoading ? (
                                        <span className="summary-skeleton" />
                                    ) : summary.TRANSACTION_COUNT ? (
                                        `${summary.TRANSACTION_COUNT}`
                                    ) : (
                                        'NA'
                                    )}
                                    {/* <span className="indicator">0.34%</span> */}
                                </p>
                            </div>
                            <div className="summary-icon">
                                <img
                                    src={TransactionCount}
                                    alt="TRANSACTIONS COUNT"
                                />
                            </div>
                        </li>
                        <li className="summary-card">
                            <div className="summary-label">
                                <label>SUCCESS COUNT</label>
                                <p>
                                    {summary.isLoading ? (
                                        <span className="summary-skeleton" />
                                    ) : summary.SUCCESSFUL_TRANSACTION_COUNT ? (
                                        `${summary.SUCCESSFUL_TRANSACTION_COUNT}`
                                    ) : (
                                        'NA'
                                    )}
                                </p>
                            </div>
                            <div className="summary-icon">
                                <img src={Transaction} alt="STATUS TREND" />
                            </div>
                        </li>
                        <li className="summary-card">
                            <div className="summary-label">
                                <label>SUCCESS RATE</label>
                                <p>
                                    {summary.isLoading ? (
                                        <span className="summary-skeleton" />
                                    ) : summary.SUCCESS_RATE ? (
                                        `${summary.SUCCESS_RATE} %`
                                    ) : (
                                        'NA'
                                    )}
                                </p>
                            </div>
                            <div className="summary-icon">
                                <img src={SuccessRate} alt="SUCCESS RATE" />
                            </div>
                        </li>
                    </ul>
                    <ul className="charts-list">
                        <li>
                            {GMV && GMV.timeSeriesValues ? (
                                <GraphSwitch
                                    defaultType="line"
                                    title="PAYMENT VOLUME"
                                    canSwitch
                                    data={GMV}
                                    legends={bankLegends}
                                    staticPayValues={staticData.payValues}
                                />
                            ) : (
                                <img
                                    className="graph-switch-chart-skeleton"
                                    src={lineLoader}
                                    alt="loading"
                                />
                            )}
                        </li>
                        <li>
                            {TRANSACTIONS_COUNT &&
                            TRANSACTIONS_COUNT.timeSeriesValues ? (
                                <GraphSwitch
                                    defaultType="bar"
                                    title="TRANSACTIONS COUNT"
                                    canSwitch
                                    data={TRANSACTIONS_COUNT}
                                    legends={barLegends}
                                    staticPayValues={staticData.payValues}
                                />
                            ) : (
                                <img
                                    className="graph-switch-chart-skeleton"
                                    src={barLoader}
                                    alt="loading"
                                />
                            )}
                        </li>
                        <li>
                            {STATUS_TREND && STATUS_TREND.timeSeriesValues ? (
                                <GraphSwitch
                                    defaultType="area"
                                    title="STATUS TREND"
                                    data={STATUS_TREND}
                                    legends={trendLegends}
                                    staticPayValues={staticData.payValues}
                                />
                            ) : (
                                <img
                                    className="graph-switch-chart-skeleton"
                                    src={lineLoader}
                                    alt="loading"
                                />
                            )}
                        </li>
                        <li>
                            {SUCCESS_RATE && SUCCESS_RATE.timeSeriesValues ? (
                                <GraphSwitch
                                    defaultType="bar"
                                    title="SUCCESS RATE"
                                    canSwitch
                                    data={SUCCESS_RATE}
                                    legends={barLegends}
                                    staticPayValues={staticData.payValues}
                                />
                            ) : (
                                <img
                                    className="graph-switch-chart-skeleton"
                                    src={barLoader}
                                    alt="loading"
                                />
                            )}
                        </li>
                    </ul>
                    {isEmpty(paymentModeWise) ? (
                        <img
                            className="big-chart-skeleton"
                            src={lineBigLoader}
                            alt="loading"
                        />
                    ) : (
                        <CardWithTab
                            tabs={successRateTab}
                            data={paymentModeWise}
                            title="PAYMENT MODE WISE SUCCESS RATE"
                            content={item =>
                                item && (
                                    <SuccessCard
                                        tabData={formatPieChartData(
                                            item,
                                            staticData
                                        )}
                                    />
                                )
                            }
                        />
                    )}
                    {isEmpty(failures) ? (
                        <img
                            className="big-chart-skeleton"
                            src={lineBigLoader}
                            alt="loading"
                        />
                    ) : (
                        <CardWithTab
                            tabs={failureRateTab}
                            data={failures}
                            customClass="failure-card"
                            title="OVER ALL FAILURES"
                            content={item =>
                                item && <FailureCard tabData={item} />
                            }
                        />
                    )}
                </>
            )}
        </div>
    );
};

const mapStateToProps = ({
    auth,
    dashboard,
    loader,
    staticData,
    merchantData,
}) => ({
    auth,
    loader,
    dashboard,
    staticData,
    merchantData,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            fetchMixedGraphRequest,
            fetchMultiGraphRequest,
            fetchSingleGraphRequest,
            fetchDashboardSummaryRequest,
            fetchUnderlyingMerchantStaticError,
            resetDashboardRequest,
            fetchMultiEntityHierarchyRequest,
            fetchEntityListRequest,
        },
        dispatch
    ),
});

Option.propTypes = {
    data: PropTypes.object,
    selectProps: PropTypes.object,
    isSelected: PropTypes.bool,
    hasValue: PropTypes.bool,
};

ValueContainer.propTypes = {
    hasValue: PropTypes.bool,
    children: PropTypes.array,
    getValue: PropTypes.func,
    options: PropTypes.array,
};

Dashboard.propTypes = {
    actions: PropTypes.object,
    auth: PropTypes.object,
    staticData: PropTypes.object,
    dashboard: PropTypes.object,
    loader: PropTypes.object,
    merchantData: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
